import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PleskConflictChoice,
  PleskCapabilityDisposition,
  PleskImportAction,
  PleskImportEntityPlan,
  PleskImportEntityType,
  PleskImportExecuteResponse,
  PleskImportOutcome,
  PleskImportPlanDto,
  PleskImportResolution,
  PleskImportResultEntry,
  PleskImportRunDetailDto,
  PleskImportRunDto,
  PleskReplacementAdvisorDto,
  PleskReplacementDecisionInput,
  PleskSnapshotDto,
} from '@cpm/shared';
import { useState } from 'react';
import { useMe } from '../api/auth.js';
import { api } from '../api/client.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  Select,
  Spinner,
  type BadgeTone,
  cx,
} from './ui.js';

/**
 * The account-import wizard (G9): snapshot picker → dry-run diff →
 * execute → results. The diff is computed server-side and nothing is
 * written until Execute; conflicts (email/name collisions, panel-side
 * deletions) are decided inline and re-previewed before executing.
 */

const ACTION_TONE: Record<PleskImportAction, BadgeTone> = {
  create: 'green',
  update: 'blue',
  skip: 'gray',
  conflict: 'red',
};

const OUTCOME_TONE: Record<PleskImportOutcome, BadgeTone> = {
  created: 'green',
  updated: 'blue',
  linked: 'blue',
  skipped: 'gray',
};

const ENTITY_LABEL: Record<PleskImportEntityType, string> = {
  plan: 'Service plans',
  client: 'Clients',
  subscription: 'Subscriptions',
};

const CHOICE_LABEL: Record<PleskConflictChoice, string> = {
  link: 'Link to existing',
  skip: 'Skip',
  recreate: 'Recreate',
};

const CAPABILITY_TONE: Record<PleskCapabilityDisposition, BadgeTone> = {
  mapped: 'green',
  'new-capability': 'red',
  'manual-cutover': 'yellow',
  'accepted-replacement': 'blue',
  'deferred-with-owner': 'yellow',
};

const CAPABILITY_LABEL: Record<PleskCapabilityDisposition, string> = {
  mapped: 'mapped',
  'new-capability': 'unsupported — new capability',
  'manual-cutover': 'manual cutover',
  'accepted-replacement': 'accepted replacement',
  'deferred-with-owner': 'parked with owner',
};

const resolutionKey = (e: { entityType: string; externalId: string }) =>
  `${e.entityType}:${e.externalId}`;

type Resolutions = Record<string, PleskConflictChoice>;

function toResolutionList(resolutions: Resolutions): PleskImportResolution[] {
  return Object.entries(resolutions).map(([key, choice]) => {
    const [entityType, externalId] = key.split(':') as [PleskImportEntityType, string];
    return { entityType, externalId, choice };
  });
}

// ── Diff table ────────────────────────────────────────────────────────

function EntityRow({
  entity,
  choice,
  onChoice,
}: {
  entity: PleskImportEntityPlan;
  choice: PleskConflictChoice | undefined;
  onChoice: (choice: PleskConflictChoice | undefined) => void;
}) {
  return (
    <li className="px-5 py-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={ACTION_TONE[entity.action]}>{entity.action}</Badge>
        <span className="font-medium text-slate-800 dark:text-slate-100">{entity.name}</span>
        <span className="text-xs text-slate-400">#{entity.externalId}</span>
        {entity.skipReason && <span className="text-xs text-slate-500">({entity.skipReason})</span>}
        {entity.conflict && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {entity.conflict.kind}
            </span>
            <Select
              className="w-auto"
              value={choice ?? ''}
              onChange={(e) => onChoice((e.target.value || undefined) as PleskConflictChoice | undefined)}
            >
              <option value="">Decide…</option>
              {entity.conflict.options.map((o) => (
                <option key={o} value={o}>
                  {CHOICE_LABEL[o]}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>
      {entity.conflict && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{entity.conflict.detail}</p>
      )}
      {entity.changes.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-xs text-slate-600 dark:text-slate-300">
          {entity.changes.map((c) => (
            <li key={c.field} className="tabular-nums">
              <span className="font-medium">{c.field}</span>: {c.from ?? '—'} →{' '}
              <span className="text-emerald-700 dark:text-emerald-400">{c.to ?? '—'}</span>
            </li>
          ))}
        </ul>
      )}
      {entity.held.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-xs text-amber-700 dark:text-amber-400">
          {entity.held.map((h) => (
            <li key={h.field}>
              <span className="font-medium">{h.field}</span> kept as “{h.panelValue ?? '—'}” (panel
              edit; Plesk has “{h.pleskValue ?? '—'}”{h.reason === 'both-modified' ? ', both sides changed' : ''})
            </li>
          ))}
        </ul>
      )}
      {entity.notes.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-xs text-slate-500 dark:text-slate-400">
          {entity.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      )}
    </li>
  );
}

function PlanReview({
  plan,
  resolutions,
  setResolutions,
  onReplacementSaved,
}: {
  plan: PleskImportPlanDto;
  resolutions: Resolutions;
  setResolutions: (r: Resolutions) => void;
  onReplacementSaved: (advisor: PleskReplacementAdvisorDto) => void;
}) {
  const groups = (['plan', 'client', 'subscription'] as const).map((t) => ({
    type: t,
    entities: plan.entities.filter((e) => e.entityType === t),
  }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 px-5 text-sm">
        {(
          [
            ['create', plan.summary.create],
            ['update', plan.summary.update],
            ['skip', plan.summary.skip],
            ['conflict', plan.summary.conflict],
          ] as const
        ).map(([action, n]) => (
          <span key={action} className="flex items-center gap-1.5">
            <Badge tone={ACTION_TONE[action]}>{action}</Badge>
            <span className="font-semibold tabular-nums">{n}</span>
          </span>
        ))}
      </div>

      {plan.deletions.length > 0 && (
        <div className="mx-5 rounded-lg bg-amber-50 px-4 py-3 text-xs text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300">
          <p className="font-medium">Deleted on the Plesk side since the last import:</p>
          <ul className="mt-1 list-inside list-disc">
            {plan.deletions.map((d) => (
              <li key={`${d.entityType}:${d.externalId}`}>
                {d.entityType} “{d.externalName ?? d.externalId}”
                {d.panelLabel
                  ? ` — panel entity “${d.panelLabel}” is kept; remove it manually if desired.`
                  : ' — its panel entity is gone too.'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {groups.map(
        (group) =>
          group.entities.length > 0 && (
            <div key={group.type}>
              <h3 className="px-5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {ENTITY_LABEL[group.type]}
              </h3>
              <ul className="mt-1 divide-y divide-slate-100 dark:divide-slate-800">
                {group.entities.map((entity) => (
                  <EntityRow
                    key={resolutionKey(entity)}
                    entity={entity}
                    choice={resolutions[resolutionKey(entity)]}
                    onChoice={(choice) => {
                      const next = { ...resolutions };
                      if (choice) next[resolutionKey(entity)] = choice;
                      else delete next[resolutionKey(entity)];
                      setResolutions(next);
                    }}
                  />
                ))}
              </ul>
            </div>
          ),
      )}

      <ReplacementAdvisor
        snapshotId={plan.snapshotId}
        advisor={plan.replacementAdvisor}
        onSaved={onReplacementSaved}
      />

      <section className="mx-5 rounded-lg bg-slate-50 px-4 py-3 text-xs ring-1 ring-inset ring-slate-200 dark:bg-slate-800/50 dark:ring-slate-700">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-100">
              Capability evidence ledger
            </h3>
            <p className="mt-0.5 text-slate-500 dark:text-slate-400">
              <strong className="text-slate-700 dark:text-slate-200">
                {plan.capabilityLedger.summary.uniqueCapabilities} unique capability groups
              </strong>{' '}
              across {plan.capabilityLedger.summary.occurrences} plan/subscription occurrences.
              Repeated rows are evidence, not separate missing features.
            </p>
          </div>
          <div className="flex gap-2">
            <a
              className="font-medium text-brand-700 hover:underline dark:text-brand-300"
              href={`/api/plesk-import/import/ledger.json?snapshotId=${encodeURIComponent(plan.snapshotId)}`}
              download
            >
              JSON
            </a>
            <a
              className="font-medium text-brand-700 hover:underline dark:text-brand-300"
              href={`/api/plesk-import/import/ledger.csv?snapshotId=${encodeURIComponent(plan.snapshotId)}`}
              download
            >
              CSV
            </a>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {(
            Object.entries(plan.capabilityLedger.summary.byDisposition) as [
              PleskCapabilityDisposition,
              { capabilities: number; occurrences: number },
            ][]
          ).map(([disposition, count]) => (
            <span key={disposition} className="flex items-center gap-1">
              <Badge tone={CAPABILITY_TONE[disposition]}>{CAPABILITY_LABEL[disposition]}</Badge>
              <span className="tabular-nums text-slate-500 dark:text-slate-400">
                {count.capabilities} groups / {count.occurrences} occurrences
              </span>
            </span>
          ))}
        </div>

        <div className="mt-3 space-y-2">
          {plan.capabilityLedger.groups.map((group) => (
            <details
              key={`${group.kind}:${group.pleskKey}`}
              className="rounded-md bg-white px-3 py-2 ring-1 ring-inset ring-slate-200 dark:bg-slate-900 dark:ring-slate-700"
            >
              <summary className="cursor-pointer list-none">
                <div className="flex flex-wrap items-center gap-2">
                  <code className="font-semibold text-slate-800 dark:text-slate-100">
                    {group.pleskKey}
                  </code>
                  <Badge>{group.kind}</Badge>
                  <Badge tone={CAPABILITY_TONE[group.disposition]}>
                    {CAPABILITY_LABEL[group.disposition]}
                  </Badge>
                  <span className="tabular-nums text-slate-500 dark:text-slate-400">
                    {group.occurrenceCount} occurrence{group.occurrenceCount === 1 ? '' : 's'}
                  </span>
                  <span className="ml-auto text-slate-500 dark:text-slate-400">
                    Owner: {group.ownerModuleName}
                  </span>
                </div>
                <p className="mt-1 text-slate-500 dark:text-slate-400">{group.explanation}</p>
                <p className="mt-1 text-slate-500 dark:text-slate-400">
                  {group.descriptor
                    ? `${group.descriptor.match} CPM ${group.descriptor.kind}: ${group.descriptor.key}`
                    : 'No CPM descriptor today'}{' '}
                  · transform: {group.valueTransform}
                </p>
              </summary>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="py-1 pr-3 font-medium">Row</th>
                      <th className="py-1 pr-3 font-medium">Plan/subscription</th>
                      <th className="py-1 pr-3 font-medium">Plesk value</th>
                      <th className="py-1 font-medium">Import result</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600 dark:text-slate-300">
                    {group.occurrences.map((item, index) => (
                      <tr key={`${item.entityType}:${item.subject}:${index}`}>
                        <td className="py-1 pr-3">{item.entityType}</td>
                        <td className="py-1 pr-3">{item.subject}</td>
                        <td className="py-1 pr-3 font-mono">{item.value}</td>
                        <td className="py-1">
                          {item.applied
                            ? `mapped to ${item.mappedValue === null ? 'unlimited' : String(item.mappedValue)}`
                            : item.note ?? CAPABILITY_LABEL[group.disposition]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

// ── Installer/site-builder replacement advisor ─────────────────────

const replacementChoice = (
  decision: PleskReplacementAdvisorDto['plans'][number]['capabilities'][number]['decision'],
): string => {
  if (!decision) return '';
  return decision.decision === 'skip'
    ? 'skip'
    : `${decision.decision}:${decision.targetId ?? ''}`;
};

function ReplacementAdvisor({
  snapshotId,
  advisor,
  onSaved,
}: {
  snapshotId: string;
  advisor: PleskReplacementAdvisorDto;
  onSaved: (advisor: PleskReplacementAdvisorDto) => void;
}) {
  const [choices, setChoices] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      advisor.plans.flatMap((plan) =>
        plan.capabilities.flatMap((capability) => {
          const value = replacementChoice(capability.decision);
          return value
            ? [[`${plan.planExternalId}:${capability.pleskKey}`, value]]
            : [];
        }),
      ),
    ),
  );
  const [notes, setNotes] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      advisor.plans.flatMap((plan) =>
        plan.capabilities.flatMap((capability) =>
          capability.decision?.note
            ? [[`${plan.planExternalId}:${capability.pleskKey}`, capability.decision.note]]
            : [],
        ),
      ),
    ),
  );

  const save = useMutation({
    mutationFn: (decisions: PleskReplacementDecisionInput[]) =>
      api.put<{ replacementAdvisor: PleskReplacementAdvisorDto }>(
        '/api/plesk-import/import/replacements',
        { snapshotId, decisions },
      ),
    onSuccess: (result) => onSaved(result.replacementAdvisor),
  });

  const decisions: PleskReplacementDecisionInput[] = [];
  for (const [key, value] of Object.entries(choices)) {
    if (!value) continue;
    const separator = key.indexOf(':');
    const planExternalId = key.slice(0, separator);
    const pleskKey = key.slice(separator + 1) as PleskReplacementDecisionInput['pleskKey'];
    if (value === 'skip') {
      decisions.push({
        planExternalId,
        pleskKey,
        decision: 'skip',
        targetId: null,
        note: notes[key]?.trim() || null,
      });
      continue;
    }
    const targetSeparator = value.indexOf(':');
    decisions.push({
      planExternalId,
      pleskKey,
      decision: value.slice(
        0,
        targetSeparator,
      ) as PleskReplacementDecisionInput['decision'],
      targetId: value.slice(targetSeparator + 1),
      note: notes[key]?.trim() || null,
    });
  }
  const externalChoiceMissingOwner = Object.entries(choices).some(
    ([key, value]) =>
      value.startsWith('external-service:') && !(notes[key]?.trim()),
  );
  const optionById = new Map(
    [...advisor.blueprints, ...advisor.alternatives].map((option) => [option.id, option]),
  );

  return (
    <section className="mx-5 rounded-lg bg-indigo-50/60 px-4 py-4 text-xs ring-1 ring-inset ring-indigo-200 dark:bg-indigo-500/10 dark:ring-indigo-500/30">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Marketplace replacement advisor
          </h3>
          <p className="mt-1 max-w-4xl text-slate-600 dark:text-slate-300">
            Choose a CPM blueprint, a named external path, or an explicit skip for every
            affected plan and key. Preview remains read-only; <strong>Save decisions</strong>{' '}
            stores these choices by Plesk plan identity so later snapshots reuse them.
          </p>
        </div>
        <div className="text-right tabular-nums text-slate-600 dark:text-slate-300">
          <p>
            {advisor.summary.decidedSlots}/{advisor.summary.decisionSlots} decisions ·{' '}
            {advisor.summary.decidedOccurrences}/{advisor.summary.occurrences} rows
          </p>
          <Badge tone={advisor.summary.complete ? 'green' : 'yellow'}>
            {advisor.summary.complete
              ? '33 rows / 7 keys decided'
              : 'decisions incomplete'}
          </Badge>
        </div>
      </div>

      <div className="mt-3 grid gap-2 md:grid-cols-3">
        {advisor.inventoryEvidence.map((evidence) => (
          <div
            key={evidence.product}
            className="rounded-md bg-white/80 px-3 py-2 ring-1 ring-inset ring-slate-200 dark:bg-slate-900/70 dark:ring-slate-700"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-slate-800 dark:text-slate-100">
                {evidence.product}
              </span>
              <Badge
                tone={
                  evidence.status === 'review-required'
                    ? 'yellow'
                    : evidence.status === 'no-active-objects'
                      ? 'green'
                      : 'blue'
                }
              >
                {evidence.status}
              </Badge>
            </div>
            <p className="mt-1 text-slate-500 dark:text-slate-400">{evidence.detail}</p>
            <details className="mt-1 text-slate-500 dark:text-slate-400">
              <summary className="cursor-pointer font-medium">
                Evidence
                {evidence.activeObjectCount !== null
                  ? ` · ${evidence.activeObjectCount} active object(s)`
                  : ''}
              </summary>
              <ul className="mt-1 list-inside list-disc">
                {evidence.evidence.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </details>
          </div>
        ))}
      </div>

      <div className="mt-3 space-y-2">
        {advisor.plans.map((plan) => (
          <details
            key={plan.planExternalId}
            className="rounded-md bg-white px-3 py-2 ring-1 ring-inset ring-slate-200 dark:bg-slate-900 dark:ring-slate-700"
          >
            <summary className="cursor-pointer font-semibold text-slate-800 dark:text-slate-100">
              {plan.planName} · {plan.capabilities.length} decisions
            </summary>
            <div className="mt-2 space-y-3">
              {plan.capabilities.map((capability) => {
                const key = `${plan.planExternalId}:${capability.pleskKey}`;
                const value = choices[key] ?? '';
                const targetId = value.includes(':') ? value.slice(value.indexOf(':') + 1) : null;
                const selected = targetId ? optionById.get(targetId) : null;
                return (
                  <div
                    key={capability.pleskKey}
                    className="grid gap-2 border-t border-slate-100 pt-2 first:border-0 first:pt-0 dark:border-slate-800 md:grid-cols-[minmax(15rem,1fr)_minmax(16rem,1fr)]"
                  >
                    <div>
                      <code className="font-semibold text-slate-800 dark:text-slate-100">
                        {capability.pleskKey}
                      </code>
                      <p className="mt-0.5 text-slate-500 dark:text-slate-400">
                        {capability.occurrenceCount} row
                        {capability.occurrenceCount === 1 ? '' : 's'} · {capability.explanation}
                      </p>
                    </div>
                    <div>
                      <Select
                        value={value}
                        onChange={(event) =>
                          setChoices((current) => ({
                            ...current,
                            [key]: event.target.value,
                          }))
                        }
                        aria-label={`Replacement for ${plan.planName} ${capability.pleskKey}`}
                      >
                        <option value="">Decide…</option>
                        {capability.allowedBlueprintIds.length > 0 && (
                          <optgroup label="CPM blueprints">
                            {capability.allowedBlueprintIds.map((id) => (
                              <option key={id} value={`blueprint:${id}`}>
                                {optionById.get(id)?.name ?? id}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {capability.allowedExternalAlternativeIds.length > 0 && (
                          <optgroup label="External services">
                            {capability.allowedExternalAlternativeIds.map((id) => (
                              <option key={id} value={`external-service:${id}`}>
                                {optionById.get(id)?.name ?? id}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        <option value="skip">Explicit skip</option>
                      </Select>
                      {selected && (
                        <div className="mt-1.5 rounded bg-slate-50 px-2.5 py-2 text-slate-500 dark:bg-slate-800/70 dark:text-slate-400">
                          <p>{selected.summary}</p>
                          <p className="mt-1">
                            <strong>Owner:</strong> {selected.maintenanceOwner}
                          </p>
                          <ol className="mt-1 list-inside list-decimal">
                            {selected.exportImportChecklist.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                      {value.startsWith('external-service:') && (
                        <div className="mt-2">
                          <Label htmlFor={`replacement-owner-${key}`}>
                            External provider and accountable owner
                          </Label>
                          <Input
                            id={`replacement-owner-${key}`}
                            value={notes[key] ?? ''}
                            placeholder="Example: Provider name — Customer owner / billing owner"
                            onChange={(event) =>
                              setNotes((current) => ({
                                ...current,
                                [key]: event.target.value,
                              }))
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        ))}
      </div>

      {save.error && (
        <div className="mt-3">
          <ErrorNote error={save.error} />
        </div>
      )}
      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="text-slate-500 dark:text-slate-400">
          {advisor.noSilentAbandonment
            ? 'Inventory evidence and saved choices account for every marketplace row without a silent customer-object gap.'
            : 'Closure remains blocked until every decision is saved and any review-required inventory evidence is resolved.'}
        </p>
        <Button
          variant="secondary"
          loading={save.isPending}
          disabled={decisions.length === 0 || externalChoiceMissingOwner}
          onClick={() => save.mutate(decisions)}
        >
          Save decisions
        </Button>
      </div>
    </section>
  );
}

// ── Results ───────────────────────────────────────────────────────────

function ResultsList({ results }: { results: PleskImportResultEntry[] }) {
  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
      {results.map((r) => (
        <li key={`${r.entityType}:${r.externalId}`} className="flex flex-wrap items-center gap-2 px-5 py-2 text-sm">
          <Badge tone={OUTCOME_TONE[r.outcome]}>{r.outcome}</Badge>
          <span className="text-xs uppercase tracking-wide text-slate-400">{r.entityType}</span>
          <span className="font-medium text-slate-800 dark:text-slate-100">{r.name}</span>
          {r.detail && <span className="text-xs text-slate-500 dark:text-slate-400">{r.detail}</span>}
        </li>
      ))}
    </ul>
  );
}

// ── Wizard ────────────────────────────────────────────────────────────

export function ImportWizardCard() {
  const me = useMe();
  const queryClient = useQueryClient();
  const isAdmin = me.data?.role === 'admin';

  const snapshots = useQuery({
    queryKey: ['plesk-import', 'snapshots'],
    queryFn: () => api.get<PleskSnapshotDto[]>('/api/plesk-import/snapshots'),
  });
  const importable = (snapshots.data ?? []).filter(
    (s) => s.status === 'completed' || s.status === 'partial',
  );

  const [snapshotId, setSnapshotId] = useState<string | null>(null);
  const [plan, setPlan] = useState<PleskImportPlanDto | null>(null);
  const [resolutions, setResolutions] = useState<Resolutions>({});
  const [outcome, setOutcome] = useState<PleskImportExecuteResponse | null>(null);

  const dryRun = useMutation({
    mutationFn: (args: { snapshotId: string; resolutions: PleskImportResolution[] }) =>
      api.post<{ plan: PleskImportPlanDto }>('/api/plesk-import/import/dry-run', args),
    onSuccess: (data) => {
      setPlan(data.plan);
      setOutcome(null);
    },
  });

  const execute = useMutation({
    mutationFn: (args: { snapshotId: string; resolutions: PleskImportResolution[] }) =>
      api.post<PleskImportExecuteResponse>('/api/plesk-import/import/execute', args),
    onSuccess: (data) => {
      setOutcome(data);
      setPlan(null);
      void queryClient.invalidateQueries({ queryKey: ['plesk-import', 'import-runs'] });
      // Imported entities appear across the panel.
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['plans'] });
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });

  const step: 'pick' | 'review' | 'done' = outcome ? 'done' : plan ? 'review' : 'pick';
  const unresolved =
    plan?.entities.filter((e) => e.action === 'conflict' && !resolutions[resolutionKey(e)]).length ??
    0;

  const reset = () => {
    setPlan(null);
    setOutcome(null);
    setResolutions({});
  };

  return (
    <Card>
      <CardHeader
        title="Account import"
        description="Turn a snapshot into panel clients, service plans and subscriptions. Idempotent — re-running updates instead of duplicating. Workloads, mail and DNS stay on Plesk until migrated separately."
        actions={
          step !== 'pick' && (
            <Button variant="ghost" onClick={reset}>
              Start over
            </Button>
          )
        }
      />

      {(dryRun.error ?? execute.error) != null && (
        <div className="px-5 pt-3">
          <ErrorNote error={dryRun.error ?? execute.error} />
        </div>
      )}

      {step === 'pick' &&
        (snapshots.isPending ? (
          <Spinner />
        ) : importable.length === 0 ? (
          <EmptyState
            title="No importable snapshot"
            description="Run an inventory snapshot first — the import always works from a frozen snapshot, never live Plesk data."
          />
        ) : (
          <div className="space-y-3 px-5 py-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              1. Pick the snapshot to import — nothing is written until you execute.
            </p>
            <ul className="space-y-2">
              {importable.map((snap) => (
                <li key={snap.id}>
                  <label
                    className={cx(
                      'flex cursor-pointer items-center gap-3 rounded-lg px-4 py-3 ring-1 ring-inset transition-colors',
                      snapshotId === snap.id
                        ? 'bg-brand-50/50 ring-brand-500 dark:bg-brand-500/10'
                        : 'ring-slate-200 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800/50',
                    )}
                  >
                    <input
                      type="radio"
                      name="import-snapshot"
                      className="size-4 border-slate-300 text-brand-600 focus:ring-brand-500"
                      checked={snapshotId === snap.id}
                      onChange={() => setSnapshotId(snap.id)}
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {new Date(snap.startedAt).toLocaleString()}
                    </span>
                    {snap.status === 'partial' && (
                      <Badge tone="yellow">partial — {snap.warnings.length} warning(s)</Badge>
                    )}
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {snap.stats
                        ? `${snap.stats.counts.customer ?? 0} customers · ${snap.stats.counts.service_plan ?? 0} plans · ${snap.stats.counts.subscription ?? 0} subscriptions`
                        : ''}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <Button
                disabled={!snapshotId}
                loading={dryRun.isPending}
                onClick={() =>
                  snapshotId && dryRun.mutate({ snapshotId, resolutions: [] })
                }
              >
                Preview import (dry run)
              </Button>
            </div>
          </div>
        ))}

      {step === 'review' && plan && (
        <div className="space-y-4 py-4">
          <p className="px-5 text-sm text-slate-600 dark:text-slate-300">
            2. Review the diff. Conflicts need a decision; unresolved conflicts are skipped. Nothing
            has been written yet.
          </p>
          <PlanReview
            plan={plan}
            resolutions={resolutions}
            setResolutions={setResolutions}
            onReplacementSaved={(replacementAdvisor) =>
              setPlan({ ...plan, replacementAdvisor })
            }
          />
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 pt-4 dark:border-slate-800">
            {unresolved > 0 && (
              <span className="mr-auto text-xs text-amber-700 dark:text-amber-400">
                {unresolved} unresolved conflict(s) will be skipped.
              </span>
            )}
            <Button
              variant="secondary"
              loading={dryRun.isPending}
              onClick={() =>
                dryRun.mutate({
                  snapshotId: plan.snapshotId,
                  resolutions: toResolutionList(resolutions),
                })
              }
            >
              Re-check with decisions
            </Button>
            <Button
              loading={execute.isPending}
              disabled={!isAdmin}
              title={isAdmin ? undefined : 'Executing an import needs an administrator'}
              onClick={() =>
                execute.mutate({
                  snapshotId: plan.snapshotId,
                  resolutions: toResolutionList(resolutions),
                })
              }
            >
              Execute import
            </Button>
          </div>
        </div>
      )}

      {step === 'done' && outcome && (
        <div className="space-y-3 py-4">
          <div className="flex flex-wrap items-center gap-4 px-5 text-sm">
            <Badge tone="green">completed</Badge>
            {(Object.entries(outcome.run.summary ?? {}) as [PleskImportOutcome, number][]).map(
              ([key, n]) => (
                <span key={key} className="flex items-center gap-1.5">
                  <Badge tone={OUTCOME_TONE[key]}>{key}</Badge>
                  <span className="font-semibold tabular-nums">{n}</span>
                </span>
              ),
            )}
          </div>
          <ResultsList results={outcome.results} />
        </div>
      )}
    </Card>
  );
}

// ── Import history ────────────────────────────────────────────────────

export function ImportRunsCard() {
  const runs = useQuery({
    queryKey: ['plesk-import', 'import-runs'],
    queryFn: () => api.get<PleskImportRunDto[]>('/api/plesk-import/import/runs'),
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const detail = useQuery({
    queryKey: ['plesk-import', 'import-runs', openId],
    queryFn: () => api.get<PleskImportRunDetailDto>(`/api/plesk-import/import/runs/${openId}`),
    enabled: openId !== null,
  });

  if (runs.isPending || runs.error || runs.data.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Import history"
        description="Every executed account import, with its applied plan and per-entity results."
      />
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {runs.data.map((run) => (
          <li key={run.id}>
            <button
              type="button"
              className="flex w-full items-center gap-3 px-5 py-3 text-left text-sm transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
              onClick={() => setOpenId(openId === run.id ? null : run.id)}
            >
              <Badge tone={run.status === 'completed' ? 'green' : 'red'}>{run.status}</Badge>
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {new Date(run.startedAt).toLocaleString()}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {run.error ??
                  Object.entries(run.summary ?? {})
                    .map(([k, n]) => `${n} ${k}`)
                    .join(' · ')}
              </span>
            </button>
            {openId === run.id &&
              (detail.isPending ? (
                <Spinner />
              ) : detail.error ? (
                <div className="px-5 pb-3">
                  <ErrorNote error={detail.error} />
                </div>
              ) : (
                detail.data.results && <ResultsList results={detail.data.results} />
              ))}
          </li>
        ))}
      </ul>
    </Card>
  );
}
